import { ChevronDown, HelpCircle, Layers } from "lucide-react";
import { useState } from "react";

import { cn } from "../../lib/cn.js";

/**
 * Smooth height (0 -> auto) collapse/expand using the CSS grid-rows trick — no JS height
 * measurement needed, works for arbitrary/changing content. Same technique as
 * `ecom_code`'s `ReportGuideAccordion`.
 */
function Collapsible({
  open,
  duration = "duration-300",
  children,
}: {
  readonly open: boolean;
  readonly duration?: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className={cn("grid transition-[grid-template-rows] ease-in-out", duration)} style={{ gridTemplateRows: open ? "1fr" : "0fr" }}>
      <div className="overflow-hidden">{children}</div>
    </div>
  );
}

/**
 * The report guide panel behind `ReportGuideToggleButton` — two collapsible sections,
 * "What this report shows" and "How ... are calculated". Ported from `ecom_code`'s
 * `ReportGuideAccordion`, matching it closely: same shape and animation, same blue
 * ("what")/indigo ("how") accent on the section icon and badge, same "Guide"/"Logic"
 * default badge text — legacy's own hard-coded `bg-blue-50 text-blue-700` etc. is kept
 * rather than swapped for a neutral token, since the two dashboards should read as the
 * same component here; only the card/border/text tokens (`--card`, `--border`,
 * `--foreground`) are this platform's own, so the panel still sits correctly on a themed
 * page and in dark mode.
 */
export function ReportGuideAccordion({
  showGuide,
  whatTitle = "What this report shows",
  whatBadge = "Guide",
  whatContent,
  howTitle = "How metrics & calculations work",
  howBadge = "Logic",
  howContent,
}: {
  readonly showGuide: boolean;
  readonly whatTitle?: string;
  readonly whatBadge?: string;
  readonly whatContent: React.ReactNode;
  readonly howTitle?: string;
  readonly howBadge?: string;
  readonly howContent?: React.ReactNode;
}): React.JSX.Element {
  const [isWhatOpen, setIsWhatOpen] = useState(true);
  const [isHowOpen, setIsHowOpen] = useState(false);

  return (
    // Outer panel slides in/out from the header when the guide is toggled.
    <Collapsible open={showGuide} duration="duration-500">
      <div
        data-testid="report-guide-panel"
        aria-hidden={!showGuide}
        className="my-1 flex flex-col gap-3 transition-all duration-500 ease-in-out"
        style={{ opacity: showGuide ? 1 : 0, transform: showGuide ? "translateY(0)" : "translateY(-14px)" }}
      >
        <GuideSection
          open={isWhatOpen}
          onToggle={() => setIsWhatOpen((v) => !v)}
          icon={<HelpCircle className="size-4" />}
          iconTone="primary"
          title={whatTitle}
          badge={whatBadge}
        >
          {whatContent}
        </GuideSection>

        {howContent !== undefined && (
          <GuideSection
            open={isHowOpen}
            onToggle={() => setIsHowOpen((v) => !v)}
            icon={<Layers className="size-4" />}
            iconTone="secondary"
            title={howTitle}
            badge={howBadge}
          >
            {howContent}
          </GuideSection>
        )}
      </div>
    </Collapsible>
  );
}

function GuideSection({
  open,
  onToggle,
  icon,
  iconTone,
  title,
  badge,
  children,
}: {
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly icon: React.ReactNode;
  readonly iconTone: "primary" | "secondary";
  readonly title: string;
  readonly badge: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="bg-card overflow-hidden rounded-xl border shadow-sm">
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        className="bg-muted/40 hover:bg-muted/70 flex cursor-pointer items-center justify-between border-b px-4 py-3 transition-colors select-none"
      >
        <div className="flex items-center gap-2.5">
          <span
            className={cn(
              "flex size-6 items-center justify-center rounded-md",
              iconTone === "primary"
                ? "bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400"
                : "bg-indigo-100 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400",
            )}
          >
            {icon}
          </span>
          <h2 className="text-foreground text-sm font-semibold">{title}</h2>
          <span
            className={cn(
              "rounded-full border px-2 py-0.5 text-[11px] font-medium",
              iconTone === "primary"
                ? "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/60 dark:bg-blue-500/10 dark:text-blue-300"
                : "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-900/60 dark:bg-indigo-500/10 dark:text-indigo-300",
            )}
          >
            {badge}
          </span>
        </div>
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs font-medium"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        >
          <span>{open ? "Hide" : "Show"}</span>
          <ChevronDown className={cn("size-4 transition-transform duration-300 ease-in-out", open && "rotate-180")} />
        </button>
      </div>
      <Collapsible open={open}>
        <div className="text-muted-foreground p-4 text-sm leading-relaxed">{children}</div>
      </Collapsible>
    </div>
  );
}
